import { fetchApi } from './api';
import type {
    AgedStockItem,
    AgedStockItemDetail,
    AgedStockFilterOptions,
    AgedStockFilters,
    AgedStockStats,
    AgedStockFlagStats,
    AgedStockFlaggedItem,
    AgedStockSortField,
    AgedStockSortOrder,
    LotFlagType,
} from '../types/agedStock';

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function todayStamp(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface PaginatedResponse<T> {
    items: T[];
    total: number;
}

interface ListParams {
    page: number;
    limit: number;
    sortBy: AgedStockSortField;
    sortOrder: AgedStockSortOrder;
    filters: AgedStockFilters;
}

function appendFilters(params: URLSearchParams, filters: AgedStockFilters): void {
    if (filters.companyCode) params.set('companyCode', filters.companyCode);
    if (filters.warehouseCountry) params.set('warehouseCountry', filters.warehouseCountry);
    if (filters.warehouseCode) params.set('warehouseCode', filters.warehouseCode);
    if (filters.supplierID) params.set('supplierID', filters.supplierID);
    if (filters.vendorID) params.set('vendorID', filters.vendorID);
    if (filters.search) params.set('search', filters.search);
    if (filters.lotSearch) params.set('lotSearch', filters.lotSearch);
    if (filters.businessUnit) params.set('businessUnit', filters.businessUnit);
    if (filters.parentCategory) params.set('parentCategory', filters.parentCategory);
    if (filters.category) params.set('category', filters.category);
    if (filters.ageBucket) params.set('ageBucket', filters.ageBucket);
    if (filters.minAgeDays != null) params.set('minAgeDays', String(filters.minAgeDays));
    if (filters.maxAgeDays != null) params.set('maxAgeDays', String(filters.maxAgeDays));
    if (filters.purchaseDateFrom) params.set('purchaseDateFrom', filters.purchaseDateFrom);
    if (filters.purchaseDateTo) params.set('purchaseDateTo', filters.purchaseDateTo);
    if (filters.expiryStatus) params.set('expiryStatus', filters.expiryStatus);
    if (filters.onlyFlagged) params.set('onlyFlagged', filters.onlyFlagged);
    if (filters.onlyWithLot) params.set('onlyWithLot', '1');
}

export const agedStockApi = {
    async list(params: ListParams): Promise<PaginatedResponse<AgedStockItem>> {
        const searchParams = new URLSearchParams();
        searchParams.set('page', String(params.page));
        searchParams.set('limit', String(params.limit));
        searchParams.set('sortBy', params.sortBy);
        searchParams.set('sortOrder', params.sortOrder);
        appendFilters(searchParams, params.filters);

        const response = await fetch(`/api/v1/aged-stock?${searchParams}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();

        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }

        return {
            items: body.data as AgedStockItem[],
            total: body.pagination?.total ?? 0,
        };
    },

    get(itemId: string, lotCode: string): Promise<AgedStockItemDetail> {
        return fetchApi<AgedStockItemDetail>(
            `/aged-stock/${encodeURIComponent(itemId)}/${encodeURIComponent(lotCode)}`
        );
    },

    getFilters(currentFilters?: AgedStockFilters): Promise<AgedStockFilterOptions> {
        const params = new URLSearchParams();
        if (currentFilters?.companyCode) params.set('companyCode', currentFilters.companyCode);
        if (currentFilters?.businessUnit) params.set('businessUnit', currentFilters.businessUnit);
        if (currentFilters?.parentCategory) params.set('parentCategory', currentFilters.parentCategory);
        if (currentFilters?.warehouseCountry) params.set('warehouseCountry', currentFilters.warehouseCountry);
        const qs = params.toString();
        return fetchApi<AgedStockFilterOptions>(`/aged-stock-filters${qs ? `?${qs}` : ''}`);
    },

    getStats(currentFilters?: AgedStockFilters): Promise<AgedStockStats> {
        const params = new URLSearchParams();
        if (currentFilters) appendFilters(params, currentFilters);
        const qs = params.toString();
        return fetchApi<AgedStockStats>(`/aged-stock-stats${qs ? `?${qs}` : ''}`);
    },

    getFlagStats(): Promise<AgedStockFlagStats> {
        return fetchApi<AgedStockFlagStats>(`/aged-stock-flag-stats`);
    },

    getFlaggedItems(flagType: LotFlagType): Promise<AgedStockFlaggedItem[]> {
        return fetchApi<AgedStockFlaggedItem[]>(
            `/aged-stock-flagged-items?flagType=${encodeURIComponent(flagType)}`
        );
    },

    async exportList(
        filters: AgedStockFilters,
        sortBy: AgedStockSortField,
        sortOrder: AgedStockSortOrder,
    ): Promise<void> {
        const searchParams = new URLSearchParams();
        searchParams.set('sortBy', sortBy);
        searchParams.set('sortOrder', sortOrder);
        appendFilters(searchParams, filters);

        const res = await fetch(`/api/v1/aged-stock-export?${searchParams}`);
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        downloadBlob(await res.blob(), `AgedStockByLot_${todayStamp()}.xlsx`);
    },

    async exportFlaggedItems(flagType: LotFlagType): Promise<void> {
        const res = await fetch(
            `/api/v1/aged-stock-flagged-items-export?flagType=${encodeURIComponent(flagType)}`
        );
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        downloadBlob(await res.blob(), `AgedStock_Flagged_${flagType}_${todayStamp()}.xlsx`);
    },
};
